import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Loader2,
  XCircle,
  CheckCircle,
  AlertTriangle,
  Heart,
} from "lucide-react";
import { useGetTicketByToken, useCancelRsvp } from "../lib/api/ticket";

export default function UnRsvp() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);

  // Fetch ticket info
  const {
    data: ticketData,
    isLoading,
    isError,
    error,
  } = useGetTicketByToken(token || "");

  // Cancel RSVP mutation
  const { mutate: cancelRsvp, isPending: isCancelling } = useCancelRsvp();

  const ticketInfo = ticketData?.data;

  const handleCancelRsvp = () => {
    if (!token) return;

    cancelRsvp(token, {
      onSuccess: () => {
        setSuccess(true);
      },
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F5F3F0] flex items-center justify-center px-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#C49355] mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (isError && !ticketInfo) {
    return (
      <div className="min-h-screen bg-[#F5F3F0] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1
            className="text-2xl font-medium text-gray-800 mb-4"
            style={{ fontFamily: "'Times New Roman', Times, serif" }}
          >
            Invalid Link
          </h1>
          <p className="text-gray-600 mb-8">
            {error?.response?.data?.message || "Invalid or expired link"}
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-8 py-3 bg-[#3d3d3d] text-white rounded-lg hover:bg-[#2d2d2d] transition"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  // Already cancelled state
  if (ticketInfo?.status === "cancelled" && !success) {
    return (
      <div className="min-h-screen bg-[#F5F3F0] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-gray-500" />
          </div>
          <h1
            className="text-2xl font-medium text-gray-800 mb-4"
            style={{ fontFamily: "'Times New Roman', Times, serif" }}
          >
            Already Cancelled
          </h1>
          <p className="text-gray-600 mb-2">
            Hi <span className="font-medium">{ticketInfo.fullName}</span>,
          </p>
          <p className="text-gray-600 mb-8">
            Your RSVP has already been cancelled. If you've changed your mind
            and would like to attend, please contact our wedding coordinators.
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-8 py-3 bg-[#3d3d3d] text-white rounded-lg hover:bg-[#2d2d2d] transition"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#F5F3F0] flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1
            className="text-2xl font-medium text-gray-800 mb-4"
            style={{ fontFamily: "'Times New Roman', Times, serif" }}
          >
            RSVP Cancelled
          </h1>
          <p className="text-gray-600 mb-2">
            Hi <span className="font-medium">{ticketInfo?.fullName}</span>,
          </p>
          <p className="text-gray-600 mb-4">
            Your RSVP has been successfully cancelled. We're sorry you won't be
            able to join us.
          </p>
          <p className="text-gray-500 text-sm mb-8">
            A confirmation email has been sent to{" "}
            <span className="font-medium">{ticketInfo?.email}</span>
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate("/")}
              className="w-full px-8 py-3 bg-[#3d3d3d] text-white rounded-lg hover:bg-[#2d2d2d] transition"
            >
              Go to Homepage
            </button>
            <p className="text-gray-400 text-xs">
              Changed your mind? Contact our wedding coordinators.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Confirmation state (show cancel button)
  return (
    <div className="min-h-screen bg-[#F5F3F0] flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 max-w-md w-full text-center">
        {/* Header */}
        <div className="mb-8">
          <Heart className="w-10 h-10 mx-auto mb-4 text-[#C49355]" />
          <h1
            className="text-2xl md:text-3xl font-medium mb-2 bg-gradient-to-r from-[#C49355] to-[#E8B46A] bg-clip-text text-transparent"
            style={{ fontFamily: "'Times New Roman', Times, serif" }}
          >
            Cancel Your RSVP
          </h1>
          <p className="text-gray-500 text-sm">Joseph & Jennifer's Wedding</p>
        </div>

        {/* Guest Info */}
        <div className="bg-[#F5F3F0] rounded-xl p-6 mb-8">
          <p className="text-gray-600 mb-1">You're cancelling RSVP for:</p>
          <p className="text-xl font-medium text-gray-800 mb-1">
            {ticketInfo?.fullName}
          </p>
          <p className="text-gray-500 text-sm">{ticketInfo?.email}</p>
        </div>

        {/* Warning */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
          <p className="text-amber-800 text-sm">
            <strong>Please note:</strong> Once cancelled, you'll need to contact
            our wedding coordinators if you change your mind and wish to attend.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleCancelRsvp}
            disabled={isCancelling}
            className="w-full px-8 py-4 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
          >
            {isCancelling ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Cancelling...
              </>
            ) : (
              "Yes, Cancel My RSVP"
            )}
          </button>

          <button
            onClick={() => navigate("/")}
            disabled={isCancelling}
            className="w-full px-8 py-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition"
          >
            No, Keep My RSVP
          </button>
        </div>

        {/* Footer */}
        <p className="text-gray-400 text-xs mt-8">
          Wedding Date: April 22, 2026
        </p>
      </div>
    </div>
  );
}
